import type { Account } from '@lyrmo/shared';
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Icon, type IconName } from '../../components/Icon';
import { usePresence } from '../../hooks/usePresence';
import { useI18n, type I18n } from '../../i18n/i18n-context';
import { cx } from '../../lib/cx';
import { useAccount, type AccountDialog } from './account-context';
import { initialsOf } from './initials';
import { UsageSummary } from './UsageSummary';

/** How the paid plan stands: when it renews or ends, or that a payment failed. */
function planStatus(
  { plan, subscription }: Account,
  { t, format }: I18n,
): { text: string; warning: boolean } | null {
  // Free, or a subscription that has lapsed.
  if (subscription?.plan !== plan) return null;
  if (subscription.status === 'past_due') return { text: t.account.paymentOverdue, warning: true };
  if (!subscription.currentPeriodEnd) return null;
  const date = format.date(subscription.currentPeriodEnd);
  return subscription.cancelAtPeriodEnd
    ? { text: t.account.ends(date), warning: true }
    : { text: t.account.renews(date), warning: false };
}

function Avatar({ email, large }: { readonly email: string | null; readonly large?: boolean }) {
  return (
    <span
      className={cx('avatar', large && 'avatar--large', !email && 'avatar--guest')}
      aria-hidden="true"
    >
      {email ? initialsOf(email) : <Icon name="user" />}
    </span>
  );
}

interface ActionProps {
  readonly icon: IconName;
  readonly accent?: boolean;
  readonly disabled?: boolean;
  readonly onClick: () => void;
  readonly children: ReactNode;
}

function Action({ icon, accent, disabled, onClick, children }: ActionProps) {
  return (
    <button
      type="button"
      className={cx('account-action', accent && 'account-action--accent')}
      disabled={disabled}
      onClick={onClick}
    >
      <Icon name={icon} />
      {children}
    </button>
  );
}

/**
 * The account in the header: the avatar, opening a panel with who is signed in, the plan, usage,
 * and what to do next.
 */
export function AccountMenu({ account }: { readonly account: Account }) {
  const i18n = useI18n();
  const { t, describeError } = i18n;
  const { openDialog, signOut, notify } = useAccount();
  const [open, setOpen] = useState(false);
  const { present, leaving } = usePresence(open);
  const [signingOut, setSigningOut] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();
  const { user, plan, usage } = account;
  const status = planStatus(account, i18n);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (event.target instanceof Node && menuRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      // Captured first, so the editor's own Escape (which leaves full screen) never sees it.
      event.stopPropagation();
      setOpen(false);
      triggerRef.current?.focus();
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [open]);

  function show(dialog: AccountDialog) {
    setOpen(false);
    // The dialog hands focus back to whatever had it, so make that the account button.
    triggerRef.current?.focus();
    openDialog(dialog, 'menu');
  }

  async function leave() {
    setSigningOut(true);
    try {
      await signOut();
      setOpen(false);
      triggerRef.current?.focus();
    } catch (error) {
      notify({ tone: 'warning', text: describeError(error) });
    } finally {
      setSigningOut(false);
    }
  }

  return (
    <div
      className="account-menu"
      ref={menuRef}
      onBlur={(event) => {
        // Tabbing out of the panel closes it.
        if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        className="account-trigger"
        aria-label={t.account.label}
        title={t.account.label}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => {
          setOpen((current) => !current);
        }}
      >
        <Avatar email={user?.email ?? null} />
      </button>

      {present && (
        <div
          className={cx('account-panel', leaving && 'is-leaving')}
          id={panelId}
          role="dialog"
          aria-label={t.account.label}
          inert={leaving}
        >
          <div className="account-identity">
            <Avatar email={user?.email ?? null} large />
            <div className="account-identity-text">
              <p className="account-name" title={user?.email}>
                {user?.email ?? t.account.guest}
              </p>
              <p className="account-plan">
                <span className="plan-badge" data-plan={plan}>
                  {t.planNames[plan]}
                </span>
                {user ? (
                  status && (
                    <span className={cx(status.warning && 'is-warning')}>{status.text}</span>
                  )
                ) : (
                  <span>{t.account.savedInBrowser}</span>
                )}
              </p>
            </div>
          </div>

          <UsageSummary usage={usage} />

          <div className="account-actions">
            {!user && (
              <Action
                icon="sign-in"
                onClick={() => {
                  show('sign-in');
                }}
              >
                {t.common.signIn}
              </Action>
            )}
            {plan === 'free' ? (
              <Action
                icon="sparkle"
                accent
                onClick={() => {
                  show('plans');
                }}
              >
                {t.account.upgradeForMore}
              </Action>
            ) : (
              <>
                <Action
                  icon="card"
                  onClick={() => {
                    show('billing');
                  }}
                >
                  {t.common.manageBilling}
                </Action>
                <Action
                  icon="sparkle"
                  onClick={() => {
                    show('plans');
                  }}
                >
                  {t.billing.comparePlans}
                </Action>
              </>
            )}
            <Action
              icon="book"
              onClick={() => {
                show('tones');
              }}
            >
              {t.tones.title}
            </Action>
            <Action
              icon="lines"
              onClick={() => {
                show('instructions');
              }}
            >
              {t.instructions.title}
            </Action>
            <Action
              icon="wand"
              onClick={() => {
                show('ai-tools');
              }}
            >
              {t.aiTools.title}
            </Action>
            <Action
              icon="key"
              onClick={() => {
                show('api-keys');
              }}
            >
              {t.apiKeys.title}
            </Action>
            {user && (
              <Action icon="sign-out" disabled={signingOut} onClick={() => void leave()}>
                {signingOut ? t.common.signingOut : t.common.signOut}
              </Action>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
